/**
 * TNE 2026, © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/hooks/useKioskMode.ts
 *
 * Rôle :
 *   Hook de détection du mode KIOSQUE (borne publique TNE 2026 sur
 *   écran géant, posée à l'accueil ou hall central). Lit le paramètre
 *   URL `?kiosk=1` au premier chargement et le persiste en
 *   `sessionStorage`. Une fois activé, le mode reste actif pour toute
 *   la session navigateur (jusqu'à fermeture de l'onglet ou wipe
 *   sessionStorage). Permet à Chrome en `--kiosk` plein écran de
 *   conserver le mode même après navigation profonde.
 *
 *   **Important** : le mode kiosque n'est considéré "actif" QUE si
 *   l'utilisateur connecté est plastron (compte de démo §5 2026-05-20).
 *   Defense in depth : si quelqu'un copie un lien `?kiosk=1` sur son
 *   téléphone perso, ne se déclenche pas. La borne doit être pré-
 *   loggée avec un compte plastron (cf. CLAUDE.md §5 entrée 2026-05-25
 *   sur la borne).
 *
 *   Paramètres URL acceptés (1er chargement uniquement) :
 *   - `?kiosk=1`  → active le mode kiosque
 *   - `?idle=N`   → délai d'inactivité avant auto-retour /mon-espace
 *                   (en MINUTES, défaut 3, borné [1, 60])
 *
 *   Comportement observé une fois `enabled=true` :
 *   - `PlastronWelcomeModal` ne s'ouvre pas (sinon impose un clic
 *     manuel après chaque login)
 *   - `PlastronGuardModal` masque le bouton « Se créer un vrai compte »
 *     (évite logout accidentel)
 *   - `<KioskInactivityWatcher>` redirige vers `/mon-espace` après
 *     `idleMinutes` d'inactivité (écoute mouse/touch/key/wheel/scroll)
 *
 *   Limite cohérente §22.7 : ce hook NE PROVOQUE PAS lui-même
 *   l'activation côté serveur. Le serveur reste agnostique, c'est
 *   purement une UX côté navigateur de la borne.
 *
 * Voir : CLAUDE.md §5 entrée 2026-05-25 (borne plastron), §5 entrée
 *        2026-05-20 (compte plastron + PlastronWelcomeModal +
 *        PlastronGuardModal), §10.10 RGPD (le mode kiosque ne change
 *        AUCUNE règle d'écriture serveur, toutes les protections
 *        `assert_not_plastron` restent intactes).
 *
 * @author Mika
 * @since 2026-05-25
 */

import { useEffect, useState } from 'react';
import { useVisitorSession } from '@/hooks/useVisitorSession';

const KIOSK_ENABLED_KEY = 'tne.kiosk.mode';
const KIOSK_IDLE_KEY    = 'tne.kiosk.idleMinutes';
const DEFAULT_IDLE_MIN  = 3;
const MIN_IDLE_MIN      = 1;
const MAX_IDLE_MIN      = 60;

/** État retourné par `useKioskMode` (props read-only pour les consommateurs). */
export interface KioskModeState {
  /** `true` UNIQUEMENT si role=plastron ET ?kiosk=1 vu au 1ᵉʳ chargement. */
  readonly enabled: boolean;
  /** Délai d'inactivité (en minutes) avant auto-retour /mon-espace. */
  readonly idleMinutes: number;
}

/**
 * Lit `?kiosk=1` + `?idle=N` dans l'URL au 1ᵉʳ chargement et persiste
 * en sessionStorage (défense contre la navigation profonde qui efface
 * la query string). Retourne l'état activé/désactivé du mode kiosque.
 *
 * SSR-safe : en SSR, `window` est undefined → retourne `{enabled:false}`.
 */
export function useKioskMode(): KioskModeState {
  const { role, status } = useVisitorSession();

  // État local, initialisé au 1er render depuis sessionStorage + URL.
  const [state, setState] = useState<{
    requested: boolean;
    idleMinutes: number;
  }>(() => readKioskFlagsFromStorage());

  // Au 1ᵉʳ chargement, on hydrate depuis l'URL si `?kiosk=1` présent.
  // Best-effort : si sessionStorage indispo (mode privé / quota), le
  // mode kiosque ne tiendra que cette page courante, pas grave pour
  // une borne en mode plein écran qui ne navigue pas trop.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const url = new URL(window.location.href);
      const kioskParam = url.searchParams.get('kiosk');
      const idleParam  = url.searchParams.get('idle');
      let nextRequested = state.requested;
      let nextIdle      = state.idleMinutes;
      if (kioskParam === '1') {
        nextRequested = true;
        try {
          window.sessionStorage.setItem(KIOSK_ENABLED_KEY, '1');
        } catch {
          // sessionStorage indispo : on garde l'état local.
        }
      }
      if (idleParam !== null) {
        const parsed = Number.parseInt(idleParam, 10);
        if (Number.isFinite(parsed) && parsed >= MIN_IDLE_MIN && parsed <= MAX_IDLE_MIN) {
          nextIdle = parsed;
          try {
            window.sessionStorage.setItem(KIOSK_IDLE_KEY, String(parsed));
          } catch {
            // ignore
          }
        }
      }
      if (nextRequested !== state.requested || nextIdle !== state.idleMinutes) {
        setState({ requested: nextRequested, idleMinutes: nextIdle });
      }
    } catch {
      // URL parse error (très improbable), ignore.
    }
    // Volontairement [] : on hydrate UNE SEULE FOIS au mount, pas à
    // chaque changement d'URL (la borne plein écran ne re-passe pas
    // par `?kiosk=1` à chaque clic).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Defense in depth : enabled = true UNIQUEMENT si role=plastron.
  // Si la session est anonyme/error/loading → enabled=false (jusqu'à
  // ce que le visiteur soit identifié plastron).
  const enabled = state.requested && status === 'authenticated' && role === 'plastron';

  return {
    enabled,
    idleMinutes: state.idleMinutes,
  };
}

/**
 * Lit les flags persistés en sessionStorage (SSR-safe).
 * Utilisé comme initialiseur du `useState` pour éviter un flash.
 */
function readKioskFlagsFromStorage(): { requested: boolean; idleMinutes: number } {
  if (typeof window === 'undefined') {
    return { requested: false, idleMinutes: DEFAULT_IDLE_MIN };
  }
  let requested = false;
  let idleMinutes = DEFAULT_IDLE_MIN;
  try {
    requested = window.sessionStorage.getItem(KIOSK_ENABLED_KEY) === '1';
    const stored = window.sessionStorage.getItem(KIOSK_IDLE_KEY);
    if (stored !== null) {
      const parsed = Number.parseInt(stored, 10);
      if (Number.isFinite(parsed) && parsed >= MIN_IDLE_MIN && parsed <= MAX_IDLE_MIN) {
        idleMinutes = parsed;
      }
    }
  } catch {
    // sessionStorage indispo, on retombe sur les défauts.
  }
  return { requested, idleMinutes };
}

// ──────────────────────────────────────────────────────────────────────────
// Notes pour rétro-port NOLAS / réutilisation :
//  - Hook 100 % générique côté kiosque, utilisable dans n'importe
//    quelle PWA TNE/NOLAS où une borne publique doit éviter
//    déconnexions accidentelles et auto-reset après inactivité.
//  - Le couplage avec `useVisitorSession` (defense in depth role=plastron)
//    est spécifique au domaine. Pour NOLAS, remplacer par le rôle
//    équivalent du compte démo.
// ──────────────────────────────────────────────────────────────────────────
